<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();

const boards = useMapGetter('kanban/getBoards');
const contactItemsGetter = useMapGetter('kanban/getContactItems');

const selectedBoardId = ref(null);
const selectedStageId = ref(null);
const isAdding = ref(false);

// The contact's current Kanban memberships (one stage per board).
const memberships = computed(() => contactItemsGetter.value(props.contactId));

// Returns the membership for a given board id, if the contact is on it.
const membershipForBoard = boardId =>
  memberships.value.find(m => m.kanban_board_id === Number(boardId));

const isExistingMember = computed(
  () => !!membershipForBoard(selectedBoardId.value)
);

const boardOptions = computed(() =>
  boards.value.map(board => ({ value: board.id, label: board.name }))
);

const selectedBoard = computed(() =>
  boards.value.find(board => board.id === Number(selectedBoardId.value))
);

const stageOptions = computed(() =>
  (selectedBoard.value?.stages || [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map(stage => ({ value: stage.id, label: stage.name, color: stage.color }))
);

const canAdd = computed(
  () => !!selectedBoardId.value && !!selectedStageId.value && !isAdding.value
);

// When the board changes, pick the membership stage if the contact already
// belongs to that board; otherwise default to the first stage. This single
// rule covers both the user switching boards and the initial pre-selection,
// so the watcher never clobbers a membership-based selection.
watch(selectedBoardId, () => {
  const membership = membershipForBoard(selectedBoardId.value);
  selectedStageId.value =
    membership?.kanban_stage_id || stageOptions.value[0]?.value || null;
});

// Pre-select the first membership's board once data is loaded.
const applyPreselection = () => {
  if (selectedBoardId.value) return;
  if (memberships.value.length) {
    selectedBoardId.value = memberships.value[0].kanban_board_id;
  }
};

watch([memberships, boards], applyPreselection, { immediate: true });

const loadData = () => {
  store.dispatch('kanban/getBoards');
  store.dispatch('kanban/getContactItems', props.contactId);
};

const addToBoard = async () => {
  if (!canAdd.value) return;
  isAdding.value = true;
  try {
    await store.dispatch('kanban/addItem', {
      boardId: selectedBoardId.value,
      contact_id: props.contactId,
      kanban_stage_id: selectedStageId.value,
    });
    useAlert(t('KANBAN.CONTACT_PANEL.SUCCESS'));
    // Refresh memberships so the panel and ContactInfo chips stay in sync.
    store.dispatch('kanban/getContactItems', props.contactId);
  } catch (error) {
    useAlert(t('KANBAN.CONTACT_PANEL.ERROR'));
  } finally {
    isAdding.value = false;
  }
};

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      selectedBoardId.value = null;
      selectedStageId.value = null;
      loadData();
    }
  }
);

onMounted(loadData);
</script>

<template>
  <div class="flex flex-col gap-3 p-2">
    <p v-if="!boards.length" class="text-sm text-center text-n-slate-11">
      {{ t('KANBAN.CONTACT_PANEL.NO_BOARDS') }}
    </p>
    <template v-else>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.CONTACT_PANEL.BOARD') }}
        </label>
        <ComboBox
          v-model="selectedBoardId"
          :options="boardOptions"
          :placeholder="t('KANBAN.CONTACT_PANEL.SELECT_BOARD')"
        />
      </div>
      <div v-if="selectedBoardId" class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.CONTACT_PANEL.STAGE') }}
        </label>
        <ComboBox
          v-model="selectedStageId"
          :options="stageOptions"
          :placeholder="t('KANBAN.CONTACT_PANEL.SELECT_STAGE')"
        />
      </div>
      <NextButton
        sm
        blue
        :icon="isExistingMember ? 'i-lucide-arrow-right-left' : 'i-lucide-plus'"
        :label="
          isExistingMember
            ? t('KANBAN.CONTACT_PANEL.UPDATE_BUTTON')
            : t('KANBAN.CONTACT_PANEL.ADD_BUTTON')
        "
        :is-loading="isAdding"
        :disabled="!canAdd"
        @click="addToBoard"
      />
    </template>
  </div>
</template>
